import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {App, Button, Input, Modal} from "antd";
import {DeleteOutlined} from "@ant-design/icons";
import type {ButtonProps} from "antd";
import type {FormikHelpers, FormikValues} from "formik";
import {useTranslation} from "react-i18next";
import {
    FormOrientation,
    RenderAntdGraphQLList as WidgetGraphQLList,
    RenderAntdStandaloneForm
} from "@ib-tehnologije/react-widgets";
import type {
    ITableColumnDefinition,
    ITenantCreating,
    ITenantFormLayout,
    ITenantUpdating
} from "@ib-tehnologije/react-widgets";
import {
    buildUserFacingErrorLabels,
    containsNonFiniteNumbers,
    getUserFacingErrorMessage
} from "../userFacingErrorUtils";
import {buildDeleteConfirmation} from "./DeleteConfirmation";
import "../Styles/AdminModalForm.css";

type AdminModalFormLayout = "default" | "paired-bilingual";

export type EventFlowTableColumnDefinition<TData> = Omit<ITableColumnDefinition<TData>, "sorter"> & {
    sorter?: boolean | ((left: TData, right: TData) => number);
};

type AdminModalFormProps<TData> = {
    open: boolean;
    title: string;
    loading?: boolean;
    baseFormData?: Partial<TData>;
    formDefinition: ITenantFormLayout<TData>;
    modalWidth?: number | string;
    modalFormLayout?: AdminModalFormLayout;
    onCancel: () => void;
    onSubmit: (values: FormikValues, helpers: FormikHelpers<FormikValues>) => Promise<unknown>;
};

export function AdminModalForm<TData>({
                                          open,
                                          title,
                                          loading,
                                          baseFormData,
                                          formDefinition,
                                          modalWidth,
                                          modalFormLayout = "default",
                                          onCancel,
                                          onSubmit
                                      }: AdminModalFormProps<TData>) {
    const {t} = useTranslation();
    const formBodyClassName = modalFormLayout === "paired-bilingual"
        ? "admin-modal-form__body admin-modal-form__body--paired-bilingual"
        : "admin-modal-form__body";

    return (
        <Modal
            destroyOnHidden
            centered
            open={open}
            width={modalWidth}
            title={title}
            cancelText={t("cancel")}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" block onClick={onCancel}>
                    {t("cancel")}
                </Button>
            ]}
        >
            <div className={formBodyClassName}>
                <RenderAntdStandaloneForm<TData>
                    loading={loading ?? false}
                    baseFormData={baseFormData}
                    formDefinition={formDefinition}
                    submission={{
                        title: "save",
                        onSubmit,
                        submitButtonProps: {type: "primary"}
                    }}
                    formSettings={{
                        inline: true,
                        orientation: FormOrientation.VERTICAL
                    }}
                />
            </div>
        </Modal>
    );
}

type MutationConfig = {
    fn: (options: any) => Promise<any>;
    modelKey: string;
    formDataToDto: (values: FormikValues) => unknown;
    formDefinition: ITenantFormLayout<any>;
};

const throwIfGraphQLErrors = (response: any) => {
    if (response?.errors?.length) {
        throw response;
    }

    return response;
};

const isFieldValidationError = (error: any) =>
    !!error?.errors && !Array.isArray(error.errors) && typeof error.errors === "object";

const titleFor = (t: (key: string, options?: any) => string, prefix: "adding" | "editing", formDefinition?: ITenantFormLayout<any>) => {
    const firstKey = Object.keys(formDefinition ?? {})[0];
    const fallback = prefix === "adding" ? t("create") : t("edit");

    if (!firstKey) {
        return fallback;
    }

    return t(`${prefix}-${firstKey}`, {defaultValue: fallback});
};

const itemsFromTableData = (tableData: any) => {
    if (Array.isArray(tableData)) {
        return tableData;
    }

    return tableData?.items ?? [];
};

const normalizeOptionalNotes = (values: FormikValues, formDefinition?: ITenantFormLayout<any>) => {
    if (!formDefinition || !Object.prototype.hasOwnProperty.call(formDefinition, "notes")) {
        return values;
    }

    return {
        ...values,
        notes: values.notes ?? ""
    };
};

const toSearchableText = (value: unknown, depth = 0): string => {
    if (value == null) {
        return "";
    }

    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return String(value).toLowerCase();
    }

    if (depth >= 2) {
        return "";
    }

    if (Array.isArray(value)) {
        return value.map(item => toSearchableText(item, depth + 1)).join(" ");
    }

    if (typeof value === "object") {
        return Object.values(value as Record<string, unknown>)
            .map(item => toSearchableText(item, depth + 1))
            .join(" ");
    }

    return "";
};

export function RenderAntdGraphQLList(props: any) {
    const {
        columns,
        hideSearchInput,
        creating,
        updating,
        query,
        loading,
        afterListEvent,
        tableData,
        getRowId,
        getNotificationKey,
        deleting,
        onAddClicked,
        onDeleteClicked,
        onEditClicked,
        onSearch,
        additionalInlineButtons,
        inlineSelectionButtons,
        createInlineButtonProps,
        deleteItemLabel,
        modalWidth,
        modalFormLayout,
        ...widgetProps
    } = props;
    const {t} = useTranslation();
    const {message, modal} = App.useApp();
    const [createOpen, setCreateOpen] = useState(false);
    const [updateOpen, setUpdateOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [createBaseFormData, setCreateBaseFormData] = useState<any>();
    const [updateBaseFormData, setUpdateBaseFormData] = useState<any>();
    const [editingItem, setEditingItem] = useState<any>();
    const [searchValue, setSearchValue] = useState("");
    const queryRef = useRef(query);
    const onSearchRef = useRef(onSearch);
    const lastServerSearchTermRef = useRef("");

    useEffect(() => {
        queryRef.current = query;
    }, [query]);

    useEffect(() => {
        onSearchRef.current = onSearch;
        if (!onSearch) {
            lastServerSearchTermRef.current = "";
        }
    }, [onSearch]);

    const createTitle = useMemo(() => titleFor(t, "adding", creating?.formDefinition), [creating?.formDefinition, t]);
    const updateTitle = useMemo(() => titleFor(t, "editing", updating?.formDefinition), [updating?.formDefinition, t]);

    const closeCreate = useCallback(() => {
        setCreateOpen(false);
        setCreateBaseFormData(undefined);
    }, []);
    const closeUpdate = useCallback(() => {
        setUpdateOpen(false);
        setEditingItem(undefined);
        setUpdateBaseFormData(undefined);
    }, []);
    const tableItems = useMemo(() => itemsFromTableData(tableData), [tableData]);
    const normalizedSearchValue = useMemo(() => searchValue.trim().toLowerCase(), [searchValue]);
    const tableDataWithLocalSearch = useMemo(() => {
        if (onSearch || !normalizedSearchValue) {
            return tableData;
        }

        const filteredItems = tableItems.filter((candidate: any) =>
            toSearchableText(candidate).includes(normalizedSearchValue));

        if (Array.isArray(tableData)) {
            return filteredItems;
        }

        return {
            ...(tableData ?? {}),
            items: filteredItems,
            totalCount: filteredItems.length
        };
    }, [normalizedSearchValue, onSearch, tableData, tableItems]);
    const localSorterColumnIds = useMemo(() => new Set<string>(
        (columns ?? [])
            .filter((column: EventFlowTableColumnDefinition<any>) => typeof column.sorter === "function")
            .map((column: EventFlowTableColumnDefinition<any>) => String(column.columnId))
    ), [columns]);
    const queryWithLocalSorting = useMemo(() => {
        if (!query || localSorterColumnIds.size === 0) {
            return query;
        }

        return {
            ...query,
            refetch: (variables?: any) => {
                const order = variables?.order;
                const orderColumnIds = Array.isArray(order)
                    ? order.flatMap(item => item && typeof item === "object" ? Object.keys(item) : [])
                    : order && typeof order === "object"
                        ? Object.keys(order)
                        : [];

                if (orderColumnIds.some(columnId => localSorterColumnIds.has(columnId))) {
                    return Promise.resolve({data: query.data});
                }

                return query.refetch?.(variables);
            }
        };
    }, [localSorterColumnIds, query]);

    const executeServerSearch = useCallback(async (term: string) => {
        const searchHandler = onSearchRef.current;
        if (!searchHandler) {
            return;
        }

        const trimmedTerm = term.trim();
        if (trimmedTerm === lastServerSearchTermRef.current) {
            return;
        }

        lastServerSearchTermRef.current = trimmedTerm;

        const where = searchHandler(trimmedTerm);
        if (!where) {
            return;
        }

        try {
            const activeQuery = queryRef.current;
            await activeQuery?.refetch?.({
                ...(activeQuery?.variables ?? {}),
                where,
                skip: 0
            });
        } catch (error) {
            console.error(error);
        }
    }, []);

    useEffect(() => {
        if (!onSearchRef.current) {
            return;
        }

        const searchTimeoutId = window.setTimeout(() => {
            void executeServerSearch(searchValue);
        }, 250);

        return () => window.clearTimeout(searchTimeoutId);
    }, [executeServerSearch, searchValue]);

    const submitMutation = useCallback(async (
        config: MutationConfig,
        eventType: "create" | "update",
        values: FormikValues,
        close: () => void,
        selectedItem?: any
    ) => {
        const errorLabels = buildUserFacingErrorLabels(t);

        try {
            setIsSubmitting(true);
            const dto = config.formDataToDto(normalizeOptionalNotes(values, config.formDefinition));
            if (containsNonFiniteNumbers(dto)) {
                const invalidValueError = Object.assign(new Error(errorLabels.invalidValues), {
                    alreadyNotified: true
                });

                message.error(errorLabels.invalidValues);
                throw invalidValueError;
            }

            const response = throwIfGraphQLErrors(await config.fn({
                variables: {
                    [config.modelKey]: dto
                }
            }));

            try {
                await afterListEvent?.({
                    eventType,
                    payload: eventType === "create"
                        ? {createDto: dto}
                        : {selectedItem, updateDto: dto}
                });
            } catch (e) {
                console.error(e);
            }

            message.success(t("successfully-saved-item"));
            await query?.refetch?.();
            close();

            return response;
        } catch (error) {
            if (isFieldValidationError(error)) {
                throw error;
            }

            if ((error as any)?.alreadyNotified) {
                throw error;
            }

            console.error(error);
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    }, [afterListEvent, message, query, t]);

    const handleAddClicked = useCallback(() => {
        if (onAddClicked) {
            onAddClicked();
            return;
        }

        setCreateBaseFormData(creating?.baseFormData ?? {});
        setCreateOpen(true);
    }, [creating?.baseFormData, onAddClicked]);

    const handleEditClicked = useCallback((item: any) => {
        const selectedItemId = item && getRowId ? getRowId(item) : undefined;
        const currentItem = selectedItemId == null
            ? item
            : tableItems.find((candidate: any) =>
                candidate && String(getRowId(candidate)) === String(selectedItemId)) ?? item;

        if (onEditClicked) {
            onEditClicked(currentItem);
            return;
        }

        setEditingItem(currentItem);
        setUpdateBaseFormData((updating as ITenantUpdating<any, any, any, any>)?.selectionToBaseFormData(currentItem) ?? {});
        setUpdateOpen(true);
    }, [getRowId, onEditClicked, tableItems, updating]);
    const handleListDoubleClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (!updating) {
            return;
        }

        const target = event.target as HTMLElement | null;
        const row = target?.closest?.("tr[data-row-key]");
        const rowKey = row?.getAttribute("data-row-key");

        if (!rowKey) {
            return;
        }

        const item = tableItems.find((candidate: any) =>
            candidate && getRowId && String(getRowId(candidate)) === rowKey);

        if (!item) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        handleEditClicked(item);
    }, [getRowId, handleEditClicked, tableItems, updating]);

    const executeDelete = useCallback(async (selectedItems: any[]) => {
        if (!selectedItems.length || (!deleting && !onDeleteClicked)) {
            return;
        }

        setIsDeleting(true);
        try {
            if (onDeleteClicked) {
                await onDeleteClicked(selectedItems);
                return;
            }

            for (const selectedItem of selectedItems) {
                const displayKey = getNotificationKey?.(selectedItem) ?? getRowId?.(selectedItem);

                try {
                    const deleteDto = deleting.selectionToDto(selectedItem);
                    throwIfGraphQLErrors(await deleting.fn({
                        variables: {
                            [deleting.modelKey]: deleteDto
                        }
                    }));
                    try {
                        await afterListEvent?.({
                            eventType: "delete",
                            payload: {selectedItem, deleteDto}
                        });
                    } catch (error) {
                        console.error(error);
                    }
                    message.success(`${t("successfully-deleted-item")}: ${displayKey}`);
                } catch (error) {
                    console.error(error);
                    const errorLabels = buildUserFacingErrorLabels(t);
                    message.error(`${t("error-deleting-item", {defaultValue: "Unable to delete item"})}: ${displayKey}. ${getUserFacingErrorMessage(error, t, errorLabels)}`);
                }
            }

            await query?.refetch?.();
        } finally {
            setIsDeleting(false);
        }
    }, [afterListEvent, deleting, getNotificationKey, getRowId, message, onDeleteClicked, query, t]);

    const confirmDelete = useCallback((selectedItems: any[]) => {
        if (!selectedItems.length) {
            return;
        }

        const itemName = selectedItems.length === 1
            ? getNotificationKey?.(selectedItems[0]) ?? getRowId?.(selectedItems[0])
            : undefined;
        const confirmation = buildDeleteConfirmation(t, {
            itemType: deleteItemLabel ?? t("delete-target-record", {defaultValue: "record"}),
            itemName,
            count: selectedItems.length > 1 ? selectedItems.length : undefined
        });

        modal.confirm({
            title: confirmation.title,
            content: confirmation.description,
            okText: confirmation.okText,
            cancelText: confirmation.cancelText,
            okButtonProps: {danger: true},
            onOk: () => executeDelete(selectedItems)
        });
    }, [deleteItemLabel, executeDelete, getNotificationKey, getRowId, modal, t]);

    const resolvedInlineButtons = useMemo<ButtonProps[] | undefined>(() => {
        if (!createInlineButtonProps) {
            return additionalInlineButtons;
        }

        return [
            ...(additionalInlineButtons ?? []),
            {
                ...createInlineButtonProps,
                onClick: handleAddClicked
            }
        ];
    }, [additionalInlineButtons, createInlineButtonProps, handleAddClicked]);

    const resolvedInlineSelectionButtons = useCallback((selectedItems: any[]) => {
        const configuredButtons = inlineSelectionButtons?.(selectedItems) ?? [];

        if (!deleting && !onDeleteClicked) {
            return configuredButtons;
        }

        return [
            ...configuredButtons,
            {
                children: t("delete", {defaultValue: "Delete"}),
                danger: true,
                icon: <DeleteOutlined/>,
                loading: isDeleting,
                onClick: () => confirmDelete(selectedItems),
                title: t("delete", {defaultValue: "Delete"})
            }
        ];
    }, [confirmDelete, deleting, inlineSelectionButtons, isDeleting, onDeleteClicked, t]);

    const createSubmit = useCallback((values: FormikValues) => {
        return submitMutation(creating, "create", values, closeCreate);
    }, [closeCreate, creating, submitMutation]);

    const updateSubmit = useCallback((values: FormikValues) => {
        return submitMutation(updating, "update", values, closeUpdate, editingItem);
    }, [closeUpdate, editingItem, submitMutation, updating]);

    return (
        <>
            {!hideSearchInput ? (
                <Input
                    allowClear
                    value={searchValue}
                    placeholder={t("search", {defaultValue: "Search"})}
                    aria-label={t("list-search", {defaultValue: "List search"})}
                    onChange={event => setSearchValue(event.target.value)}
                    style={{marginBottom: 12}}
                />
            ) : null}
            <div className="admin-graphql-list" onDoubleClickCapture={handleListDoubleClickCapture}>
                <WidgetGraphQLList
                    {...widgetProps}
                    columns={columns}
                    creating={createInlineButtonProps ? undefined : creating}
                    query={queryWithLocalSorting}
                    loading={loading || isDeleting}
                    tableData={tableDataWithLocalSearch}
                    getRowId={getRowId}
                    getNotificationKey={getNotificationKey}
                    additionalInlineButtons={resolvedInlineButtons}
                    inlineSelectionButtons={resolvedInlineSelectionButtons}
                    onAddClicked={createInlineButtonProps ? onAddClicked : (creating ? handleAddClicked : onAddClicked)}
                    onEditClicked={updating ? handleEditClicked : onEditClicked}
                />
            </div>
            {creating && (
                <AdminModalForm
                    open={createOpen}
                    title={createTitle}
                    loading={isSubmitting}
                    baseFormData={createOpen ? createBaseFormData : creating.baseFormData}
                    formDefinition={(creating as ITenantCreating<any, any, any, any>).formDefinition}
                    modalWidth={modalWidth}
                    modalFormLayout={modalFormLayout}
                    onCancel={closeCreate}
                    onSubmit={createSubmit}
                />
            )}
            {updating && (
                <AdminModalForm
                    open={updateOpen}
                    title={updateTitle}
                    loading={isSubmitting}
                    baseFormData={updateOpen ? updateBaseFormData : {}}
                    formDefinition={(updating as ITenantUpdating<any, any, any, any>).formDefinition}
                    modalWidth={modalWidth}
                    modalFormLayout={modalFormLayout}
                    onCancel={closeUpdate}
                    onSubmit={updateSubmit}
                />
            )}
        </>
    );
}
